import type { AfterContentInit, QueryList, TemplateRef } from '@angular/core';

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ContentChildren, EventEmitter, Input, Output } from '@angular/core';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { wzbBoNavigationSidebarMenuIconsDirective } from '@commons/bo-ui/navigation-sidebar-menu/navigation-sidebar-menu-icons.directive';
import { ToStringPipe } from '@commons/bo-ui/navigation-sidebar-menu/to-string.pipe';

export interface NavigationItem {
  route: string | string[];
  label?: string;
  icon?: string;
  grantedTo?: string | string[];
}

export interface NavigationAction {
  id: string | string[];
  label?: string;
  icon?: string;
  iconText?: string;
  grantedTo?: string;
  menuItems?: {
    id: string;
    label: string;
  }[];
}

/**
 *
 * Navigation Sidebar component
 * @description Vertical navigation menu
 * Themed with {@link @wizbii/backoffice-style}
 *
 * @example
 * <app-bo-navigation-sidebar-menu [items]="[{route: 'route1', label: 'Route 1', icon: 'icon1'}]" [reduced]="false" [activeRoute]="route1" (routeClicked)="handleRouteClick($event)">
 *  <img src="assets/logo.png" menuLogo />
 *  <img src="assets/icon1.png" *appBoNavigationSidebarMenuIcon="'icon1'"/>
 * </app-bo-navigation-sidebar-menu>
 */
@Component({
  selector: 'app-bo-navigation-sidebar-menu',
  templateUrl: './navigation-sidebar-menu.component.html',
  styleUrls: ['./navigation-sidebar-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatListModule, MatIconModule, MatRippleModule, MatTooltipModule, MatMenuModule, ToStringPipe],
})
export class WzbBoNavigationSidebarMenuComponent implements AfterContentInit {
  /**
   * Navigation menu items
   * * @example
   * [{
   *   route: "/page1",
   *   label: "Page 1"
   * },
   * {
   *   route: "/page2",
   *   label: "Page 2"
   * }]
   */
  @Input() items: NavigationItem[] = [];

  /**
   * Highlighted menu items, separated from items (optional)
   * @example
   * [{
   *   id: "/logout",
   *   label: "Log out",
   *   menuItems: [{id: '/test', label: 'test'}]
   * }]
   */
  @Input() actions: NavigationAction[] = [];

  /**
   * Layout state of the sidebar
   * @description
   * If true, sidebar is expanded and shows label & icons
   * If false, sidebar is collapsed and only shows icons
   */
  @Input() reduced = false;

  /**
   * Flag enabling sidebar reducibility.
   * Set to {false} if navigation items don't use icons
   */
  @Input() reducible = true;

  /**
   * Current route used for selected apparance
   * @example
   * ['/', 'route1']
   */
  @Input() activeRoute: string | string[] = '';

  /**
   * Define the action for reducing the menu (optional)
   * arrow or classic
   * @default
   * classic
   */
  @Input() reduceMode: 'classic' | 'arrow' = 'classic';

  /**
   * Define the top position of the toggle arrow
   * @example
   * 2rem
   */
  @Input() toggleArrowTopPosition = '2.5rem';

  /**
   * Define the bottom margin for footer links
   */
  @Input() footerActionsBottomMargin = '0';

  /**
   * EventEmitter when a click on an item is triggered
   */
  @Output() itemClicked = new EventEmitter<NavigationItem>();

  /**
   * EventEmitter when a menuItem is triggered
   */
  @Output() menuItemClicked = new EventEmitter<string>();

  /**
   * EventEmitter when a actionItem is triggered
   */
  @Output() actionClicked = new EventEmitter<NavigationAction>();

  /**
   * Map of icons used by navigation items
   */
  iconTemplateMap: Record<string, TemplateRef<any>> = {};

  /**
   * Templates using {@link BoNavigationIconDirective}
   */
  @ContentChildren(wzbBoNavigationSidebarMenuIconsDirective)
  iconTemplates!: QueryList<wzbBoNavigationSidebarMenuIconsDirective>;

  /**
   * Initialize {@link iconTemplateMap}
   */
  ngAfterContentInit(): void {
    this.iconTemplateMap = this.buildIconTemplateMap(this.iconTemplates);
  }

  /**
   * Builds a map of templates queried with {@link wzbBoNavigationSidebarMenuIconsDirective}
   * @returns {Record} A map of templates associated to their name
   * @param templates
   */
  buildIconTemplateMap(
    templates: QueryList<wzbBoNavigationSidebarMenuIconsDirective>
  ): Record<string, TemplateRef<any>> {
    const map: Record<string, TemplateRef<any>> = {};
    templates.forEach((templ) => {
      map[templ.name] = templ.templateRef;
    });
    return map;
  }

  /**
   * Emit an event when a nav item is clicked
   */
  onItemClick(item: NavigationItem): void {
    this.itemClicked.emit(item);
  }

  /**
   * Emit an event when an action is clicked
   */
  onActionClick(action: NavigationAction): void {
    this.actionClicked.emit(action);
  }

  /**
   * Emit an event when a menuItem is clicked
   */
  onMenuItemClick(id: string): void {
    this.menuItemClicked.emit(id);
  }

  /**
   * Switch the sidebar from reduced state to expended state and vice versa
   */
  toggle(): void {
    this.reduced = !this.reduced;
  }
}
